/**
 * @module services/zotero/authService
 * @description Authentication service for Zotero API
 *
 * All authentication goes through Supabase Edge Function proxy.
 * API keys are stored server-side in Spectral Cloud Portal for security.
 */

import { createLogger } from '../loggerService';
import { proxyZoteroRequest } from '../supabaseClient';
import { apiClient } from './apiClient';
import type { ApiKeyResponse, ServiceResult } from '@types';

const log = createLogger('api');

// =============================================================================
// AUTHENTICATION SERVICE
// =============================================================================

/**
 * Validate API key via proxy
 *
 * Gets user info via the Supabase Edge Function proxy using /keys/current.
 * The actual API key is stored server-side in Spectral Cloud Portal.
 */
export async function validateApiKey(_key: string): Promise<ServiceResult<ApiKeyResponse>> {
  try {
    const response = await proxyZoteroRequest<ApiKeyResponse>({
      path: '/keys/current',
      method: 'GET',
    });

    if (response.data) {
      return { success: true, data: response.data };
    }

    return {
      success: false,
      error: 'Failed to get user info from Zotero',
      code: 'VALIDATION_FAILED',
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Proxy validation failed';

    // Check for specific error codes
    if (message.includes('NO_API_KEY') || message.includes('not configured')) {
      return {
        success: false,
        error: 'Zotero API key not configured. Please add it in Spectral Cloud Portal settings.',
        code: 'NO_API_KEY',
      };
    }

    log.error('Proxy API key validation failed', { error: message });
    return {
      success: false,
      error: message,
      code: 'NETWORK_ERROR',
    };
  }
}

// =============================================================================
// RE-EXPORTS FROM API CLIENT
// =============================================================================

export const isAuthenticatedAsync = () => apiClient.isAuthenticatedAsync();
